/**
 * Ziel und Link-Token über die Registrierung und die E-Mail-Bestätigung hinweg (F5).
 *
 * Wer über einen Link kommt (Verkäufer-Anmeldung, Helfer-QR, Einladung), sich registriert und die Adresse im Mail-Link
 * bestätigt, öffnet die Bestätigung meist in einem neuen Tab. Der Tab-Speicher (sessionStorage) ist dann leer. Darum liegen
 * Ziel und Token im localStorage, mit Ablauf (24 Stunden), und werden nach Gebrauch geleert.
 *
 * - Ziel: nur relative, geprüfte Pfade (`safeNext`), nie eine fremde Adresse (kein offener Redirect).
 * - Token: nie in Pfad oder Abfrage (Regel 10), nur Fragment der Link-Adresse, hier im Speicher; Form wird beim Lesen geprüft.
 */

import { safeNext } from './safeNext';

/** Wie lange ein gemerktes Ziel oder Token gilt. */
export const PENDING_TTL_MS = 24 * 60 * 60 * 1000;

export const NEXT_KEY = 'barso.pending.next';
export const HELPER_TOKEN_KEY = 'barso.helper';
export const INVITATION_TOKEN_KEY = 'barso.invitation';

/** Form der Tokens aus Helfer-Links und Einladungen (URL-sicher, Base64url), alles andere wird verworfen. */
const TOKEN_PATTERN = /^[A-Za-z0-9_-]{20,200}$/;

interface Stored {
  value: string;
  expiresAt: number;
}

function parse(raw: string | null): Stored | null {
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'value' in parsed &&
      'expiresAt' in parsed &&
      typeof parsed.value === 'string' &&
      typeof parsed.expiresAt === 'number'
    ) {
      return { value: parsed.value, expiresAt: parsed.expiresAt };
    }
  } catch {
    // beschädigter Eintrag: wie nicht vorhanden behandeln
  }
  return null;
}

function remember(key: string, value: string, now: number): void {
  try {
    const entry: Stored = { value, expiresAt: now + PENDING_TTL_MS };
    window.localStorage.setItem(key, JSON.stringify(entry));
  } catch {
    // Speicher gesperrt (privater Modus): der Link aus der E-Mail funktioniert weiterhin
  }
}

function recall(key: string, now: number): string | null {
  try {
    const entry = parse(window.localStorage.getItem(key));
    // Ein Ablauf weiter als 24 Stunden in der Zukunft stammt nicht von hier (manipuliert): verwerfen
    if (entry === null || entry.expiresAt <= now || entry.expiresAt > now + PENDING_TTL_MS) {
      window.localStorage.removeItem(key);
      return null;
    }
    return entry.value;
  } catch {
    return null;
  }
}

function forget(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // nichts zu tun
  }
}

/** Merkt sich das Ziel (nur ein geprüfter, interner Pfad außer der Startseite). */
export function rememberNext(path: string, now: number = Date.now()): void {
  // Nie ein Fragment speichern: Dort stehen Link-Tokens (Regel 10), die sonst 24 Stunden als Ziel liegen blieben
  if (path === '/' || path.includes('#') || safeNext(path) !== path) return;
  remember(NEXT_KEY, path, now);
}

/** Gemerktes Ziel, solange es gültig ist (wird beim Lesen erneut geprüft), sonst `null`. */
export function pendingNext(now: number = Date.now()): string | null {
  const path = recall(NEXT_KEY, now);
  if (path === null) return null;
  if (path === '/' || path.includes('#') || safeNext(path) !== path) {
    forget(NEXT_KEY);
    return null;
  }
  return path;
}

export function clearPendingNext(): void {
  forget(NEXT_KEY);
}

/** Token eines Helfer-Links oder einer Einladung merken (nur bei gültiger Form). */
export function rememberToken(key: string, token: string, now: number = Date.now()): void {
  if (TOKEN_PATTERN.test(token)) remember(key, token, now);
}

export function recallToken(key: string, now: number = Date.now()): string | null {
  const token = recall(key, now);
  if (token === null) return null;
  if (!TOKEN_PATTERN.test(token)) {
    forget(key);
    return null;
  }
  return token;
}

export function clearToken(key: string): void {
  forget(key);
}

/**
 * Räumt Ziel und alle gemerkten Link-Tokens weg. Beim Abmelden: Am geteilten Gerät (Kassen-Laptop, Vereinsrechner)
 * soll die nächste Person weder das Ziel noch den Helfer-Link oder die Einladung der vorigen vorbelegt bekommen.
 */
export function clearAllPending(): void {
  forget(NEXT_KEY);
  forget(HELPER_TOKEN_KEY);
  forget(INVITATION_TOKEN_KEY);
}
